'use client'
import React, { forwardRef, useCallback, useRef } from 'react';

import styles from './styles.module.scss';
import Container from '@/components/primitives/container';

export type ButtonVariant = 'primary' | 'secondary' | 'transparent';

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  rootClassName?: string;
  aurora?: boolean;
  variant?: ButtonVariant;
}

const Button = forwardRef<HTMLButtonElement, Props>((props: Props, ref) => {
  const { rootClassName = '', className = '', variant = 'primary', aurora = false, disabled = false, ...restProps } = props;
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const handleRef = useCallback((node: HTMLButtonElement | null) => {
    buttonRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // 버튼이 비활성화된 경우 클릭을 막음
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Container에서 클릭이 발생했지만 실제 button이 아닌 경우에만
    // 프로그래매틱하게 button.click() 호출
    if (buttonRef.current && e.target !== buttonRef.current) {
      // 실제 button 엘리먼트의 click()을 호출하여 
      // form submit 등의 기본 동작도 함께 처리
      buttonRef.current.click();
    }
  }, [disabled]);

  return <Container
    rootClassName={`focus-ring ${rootClassName}`}
    className={`${styles.container} ${styles[variant]} ${disabled ? styles.disabled : ''} ${className}`}
    onClick={handleClick}
    aurora={aurora}
  >
    <button 
      ref={handleRef} 
      disabled={disabled}
      {...restProps} 
    />
  </Container>;
});

Button.displayName = 'Button';

export default Button;